import type { UnionOfPropertyTypes } from '@backmarket/utils/object/UnionOfPropertyTypes'

import type { Country, MonetaryAmount } from '@backmarket/http-api'
import type {
  BuybackShipping,
  OrderDetails,
} from '@backmarket/http-api/src/api-specs-buyback/customer/getCustomerSaleDetails'
import type {
  BuybackModelAction,
  BuybackModelInstruction,
  BuybackModelProduct,
  BuybackModelTimeline,
} from '@backmarket/http-api/src/api-specs-buyback/models'

export type GetRefurbisherFees = {
  batteryFee: string
}

export type GetRefurbisherInsights = {
  orders: {
    shipped: number
    delivered: number
    toBeProcessed: number
    pendingReply: number
  }
}

export const PaymentMethodCode = {
  ach: 'ach',
  bacs: 'bacs',
  card: 'card',
  sepa: 'sepa',
} as const

export type PaymentMethodCode = UnionOfPropertyTypes<typeof PaymentMethodCode>

const PaymentMethodGateway = {
  ADYEN: 'ADYEN',
  PROCESSOUT: 'PROCESSOUT',
} as const

type PaymentMethodGateway = UnionOfPropertyTypes<typeof PaymentMethodGateway>

export type PaymentMethod = {
  reference: string
  gateway: PaymentMethodGateway
  code: PaymentMethodCode
  configured: boolean
}

type CurrenciesPaymentMethod = {
  currency: string
  bankTransferPreferred: boolean
  paymentMethods?: PaymentMethod[]
}

export type GetRefurbisherPaymentMethodsConfig = {
  currenciesPaymentMethods: CurrenciesPaymentMethod[]
}

export type PostRefurbisherPaymentTokenization = {
  successUrl: string
  failureUrl: string
  cancelUrl: string
  paymentMethod: {
    code: string
    reference: string
    gateway: PaymentMethodGateway
  }
}

export type PostRefurbisherPaymentTokenizationResponse = {
  success: boolean
  reference: string
  gatewayReference: string
  gatewayTokenReference: string
}

export type RefurbisherProduct = {
  productId: string
  title: string
  image: string
  availableMarkets: Array<string>
}

export type RefurbisherQuotationsResult = {
  countryCode: string
  product: RefurbisherProduct
  listings: Array<RefurbisherQuotationsListing>
}

export type RefurbisherQuotations = {
  count: number
  next: string | null
  previous: string | null
  results: Array<RefurbisherQuotationsResult>
}

export type MerchantMarkets = {
  markets: Array<string>
}

export type PutBankTransferPreference = {
  paymentMethodCode: string
  bankTransferPreferred: boolean
}

export type QuotationGrade = {
  name: string
  code: string
}

export type RefurbisherQuotationsListing = {
  backboxPrice: MonetaryAmount | null
  grade: QuotationGrade
  hasBackbox: boolean
  price: MonetaryAmount
  publicationState: string
  tag: string
  sku: string
  isPublicationStateComputing: boolean
}

export interface QuotationGroup {
  id: string
  title: string
  listings: Array<RefurbisherQuotationsListing>
}

export interface WishList {
  countryCode: string
  title: string
  id: string
  image: string
  quotationsGroups: Array<QuotationGroup>
}

export type Offer = Record<string, { price: string | undefined; sku: string }>

type BuybackOrderDetailsIdentityDocuments = {
  required: boolean
  documents: {
    label: string
    type?: string
    file_url:
      | {
          href: string
          type: 'internal'
        }
      | {
          href: string
          type: 'external'
        }
  }[]
}

export interface BuybackShippingAddress {
  street: string
  street2: string
  postalCode: string
  city: string
  stateOrProvince: string
  country: Country
  firstName: string
  lastName: string
  companyName: string
  phoneNumber?: string
}

export type BuybackOrderDetails = {
  product: BuybackModelProduct
  order: OrderDetails
  shipping: Omit<BuybackShipping, 'deliveryAddress' | 'returnAddress'> & {
    deliveryAddress: BuybackShippingAddress
    returnAddress: BuybackShippingAddress
  }
  statusTimeline: BuybackModelTimeline[]
  identityDocuments: BuybackOrderDetailsIdentityDocuments
  instruction: BuybackModelInstruction
  actions: BuybackModelAction[]
}

export type BuybackOrderDetailsOnHoldAction = {
  id: string
  title: string
  description: string
}

export type MerchantOrder = {
  countryCode: string
  creationDate: string
  customer: { firstName: string; lastName: string }
  product: { title: string }
  sourcingOrderId: string
  state: string
  trackingNumber: string
  remainingHours?: number
}

export interface RefurbisherCounterOfferDetails {
  comment: string
  description?: string
  initialOfferPrice: MonetaryAmount
  counterOfferPrice: MonetaryAmount
  reasons: Array<string>
  authorRole: string
  attachments: Array<string>
}

export type BuybackOrderDetailsValidateOrder = {
  description?: string
  orderId: string
  orderPublicId: string
  creationDate: string
  modificationDate: string
  shippingDate: string
  suspensionDate?: string
  receivalDate?: string
  paymentDate?: string
  counterProposalDate?: string
  status: string
  market: string
  transferCertificateLink: string
  listing: {
    sku?: string
    productId: number
    title: string
    grade: string
  }
  customer: {
    firstName?: string
    lastName?: string
    phone?: string
    dateOfBirth?: string
    documents?: {
      type: string
      url: string
    }[]
  }
  returnAddress: {
    address1?: string
    address2?: string
    city?: string
    zipcode?: string
    description?: string
    country: string
  }
  originalPrice: {
    description?: string
    value: number
    currency: string
  }
  counterOfferPrice?: {
    description?: string
    value: number
    currency: string
  }
  trackingNumber?: string
  shipper?: string
  suspendReasons?: {
    description?: string
    message: string
    identifier: string
    category?: string
  }[]
  counterOfferReasons?: {
    description?: string
    message: string
    identifier: string
    category?: string
  }[]
}

export type BuybackOrderDetailsCounterOfferReason = {
  id: string
  label: string
  reasons: {
    id: string
    label: string
  }[]
}

export type BuybackOrderDetailsSuspensionReasons = {
  description?: string
  label: string
  reasons: {
    id: string
    label: string
  }[]
}
